import React, { useCallback, useMemo } from 'react';
import type { BottomTabBarProps, BottomTabNavigationOptions } from '@react-navigation/bottom-tabs';
import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, View } from 'react-native';

import { theme, TYPE_CONFIG, useResponsive, useSafeNavigation } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { Navbar } from '../components';
import { type HeaderButtonProps, useHeaderRight } from '../components';
import { MainTabBar } from '../components/MainTabBar';
import { useNavigateToLastInteractedStore } from '../context/LastInteractedStore';
import { getCustomBackButtonIcon } from './AppNavigation.backButton';
import {
  accountMessages,
  forgotToScanMessages,
  giftCardCheckoutScreenMessages,
  giftCardConfirmationScreenMessages,
  giftCardRedemptionScreenMessages,
  giftCardsStackScreenMessages,
  loyaltyHomeScreenMessages,
  redeemGiftCardScreenMessages,
  reorderHomeScreenMessages,
} from './messages';

import type {
  AppStackParamList,
  AuthStackScreenProps,
  ModalStackScreenProps,
} from './AppNavigation.props';

// Screen options for the app stack.
export const useAppStackOptions = () => {
  const modalContentStyle = useMobileTransparentScreenContentStyle();
  const modalPresentationStyle = useMobileTransparentModalScreenPresentation();

  return useMemo<Record<keyof AppStackParamList | 'Navigator', NativeStackNavigationOptions>>(
    () => ({
      Navigator: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        headerShown: false,
      },
      MainTabs: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
      },
      Modal: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        contentStyle: modalContentStyle,
        presentation: modalPresentationStyle,
      },
      Auth: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        presentation: 'modal',
      },
      Error: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        presentation: 'fullScreenModal',
      },
    }),
    [modalContentStyle, modalPresentationStyle],
  );
};

// Screen options for the main tabs.
export const useMainTabsOptions = () => {
  const { formatMessage } = useIntl();

  const { track } = useTelemetry();
  const navigation = useSafeNavigation();
  const { currentBreakpoint } = useResponsive();

  // ──────────────────────────────────────────────────────────────────────────

  const TabBar = useCallback(
    (props: BottomTabBarProps) => {
      // Bottom tabs only enabled on mobile views.
      if (!currentBreakpoint.isXS) {
        return null;
      }

      return <MainTabBar {...props} />;
    },
    [currentBreakpoint.isXS],
  );

  // Menu tab action.
  const navigateToLastInteractedStore = useNavigateToLastInteractedStore();

  const navigateToGiftCardsScreen = useCallback(() => {
    navigation?.navigate('MainTabs', {
      screen: 'GiftCardsTab',
      params: { screen: 'GiftCards' },
      initial: true,
    });
  }, [navigation]);

  return useMemo(() => {
    const sharedMainTabsOptions = {
      Navigator: DEFAULT_TABS_OPTIONS,
      TabBar,

      // NOTE: Normally we'd prefer responsive logic, over Platform -- but this
      // relates to platform specific navigation idioms. It's possible that we
      // could align on history but it needs to be reviewed more thoroughly. The
      // important thing is that browser history behaves more conventionally.
      BackBehavior: Platform.OS === 'web' ? ('history' as const) : ('firstRoute' as const), // firstRoute is default
    };

    return {
      ...sharedMainTabsOptions,

      HomeTab: {
        title: formatMessage(messages.navBarHomeTitle),
        tabBarTestID: 'nav-bar.home',
        iconName: 'IconHomeAlternative',
        onPress() {
          track('tab-bar.home');
        },
      },
      MenuTab: {
        title: formatMessage(messages.navBarMenuTitle),
        tabBarTestID: 'nav-bar.order',
        customNavigate: navigateToLastInteractedStore,
        onPress() {
          track('tab-bar.menu');
        },
        iconName: 'IconMenuBowl',
      },
      LoyaltyTab: {
        title: formatMessage(loyaltyHomeScreenMessages.screenTitle),
        tabBarTestID: 'nav-bar.loyalty',
        iconName: 'IconSgRewardBlackOutline',
        onPress() {
          track('tab-bar.loyalty');
        },
      },
      ReorderTab: {
        title: formatMessage(reorderHomeScreenMessages.screenTitle),
        tabBarTestID: 'nav-bar.reorder',
        iconName: 'IconHeartStroke',
        onPress() {
          track('tab-bar.reorder');
        },
      },
      GiftCardsTab: {
        title: formatMessage(giftCardsStackScreenMessages.screenTitle),
        tabBarTestID: 'nav-bar.gift-cards',
        customNavigate: navigateToGiftCardsScreen,
        shouldForceOnPress: true,
        iconName: 'IconPromo',
        onPress() {
          track('tab-bar.gift');
        },
      },
      AccountTab: {
        title: formatMessage(messages.navBarAccountTitle),
        tabBarTestID: 'nav-bar.account',
        iconName: 'IconAccount',
        onPress() {
          track('tab-bar.account');
        },
      },
    };
  }, [TabBar, navigateToLastInteractedStore, formatMessage, navigateToGiftCardsScreen, track]);
};

// Screen options for the home tab stack.
export const useHomeTabStackOptions = () => {
  const { formatMessage } = useIntl();

  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: true,
  });

  const homeTabScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: false,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      Home: {
        ...DEFAULT_SCREEN_OPTIONS,
        ...homeTabScreenOptions,
        customDocumentTitle: formatMessage(messages.pageTitleHome),
      },
    }),
    [homeTabScreenOptions, navigatorScreenOptions, formatMessage],
  );
};

// Screen options for the menu tab stack.
export const useMenuTabStackOptions = () => {
  const { formatMessage } = useIntl();
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions();

  const defaultHeader = useStackHeader({ mobileHeaderShown: true });

  const defaultScreenOptions = useMemo<NativeStackNavigationOptions>(
    () => ({ header: defaultHeader }),
    [defaultHeader],
  );

  const menuHeader = useStackHeader({ mobileHeaderShown: true });

  const menuHeaderScreenOptions = useMemo<NativeStackNavigationOptions>(
    () => ({ header: menuHeader }),
    [menuHeader],
  );

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      Locations: {
        ...defaultScreenOptions,
        customDocumentTitle: formatMessage(messages.pageTitleLocations),
      },
      MenuWithoutSlug: {
        ...menuHeaderScreenOptions,
      },
      Menu: {
        ...menuHeaderScreenOptions,
      },
      DeliveryMenu: {
        ...menuHeaderScreenOptions,
      },
    }),
    [defaultScreenOptions, menuHeaderScreenOptions, navigatorScreenOptions, formatMessage],
  );
};

// Screen options for the loyalty tab stack.
export const useLoyaltyTabStackOptions = () => {
  const { formatMessage } = useIntl();

  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: true,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      LoyaltyHome: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(loyaltyHomeScreenMessages.screenTitle),
        customDocumentTitle: formatMessage(loyaltyHomeScreenMessages.screenCustomDocumentTitle),
      },
    }),
    [navigatorScreenOptions, formatMessage],
  );
};

// Screen options for the reorder tab stack.
export const useReorderTabStackOptions = () => {
  const { formatMessage } = useIntl();

  return useMemo(
    () => ({
      Navigator: DEFAULT_TABS_OPTIONS as NativeStackNavigationOptions,
      Reorder: {
        ...DEFAULT_STACK_SCREEN_OPTIONS,
        headerShown: false,
        title: formatMessage(reorderHomeScreenMessages.screenTitle),
        headerTitle: formatMessage(reorderHomeScreenMessages.screenTitle),
        customDocumentTitle: formatMessage(reorderHomeScreenMessages.screenCustomDocumentTitle),
      },
    }),
    [formatMessage],
  );
};

// Screen options for the "Gift Cards" tab stack.
export const useGiftCardsTabStackOptions = () => {
  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: false,
  });
  const screenWithMobileHeaderOptions = useStackHeader({
    mobileHeaderShown: true,
  });

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      Navigator: navigatorScreenOptions,
      GiftCards: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(giftCardsStackScreenMessages.screenTitle),
        headerTitle: formatMessage(giftCardsStackScreenMessages.screenTitle),
        customDocumentTitle: formatMessage(giftCardsStackScreenMessages.screenCustomDocumentTitle),
      },
      GiftCardCheckout: {
        ...DEFAULT_SCREEN_OPTIONS,
        header: screenWithMobileHeaderOptions,
        headerBackVisible: false, // we render custom back button instead
        title: formatMessage(giftCardCheckoutScreenMessages.screenTitle),
        customDocumentTitle: formatMessage(
          giftCardCheckoutScreenMessages.screenCustomDocumentTitle,
        ),
      },
      GiftCardConfirmation: {
        ...DEFAULT_SCREEN_OPTIONS,
        header: screenWithMobileHeaderOptions,
        title: formatMessage(giftCardConfirmationScreenMessages.screenTitle),
        customDocumentTitle: formatMessage(
          giftCardConfirmationScreenMessages.screenCustomDocumentTitle,
        ),
      },
      GiftCardRedemption: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(giftCardRedemptionScreenMessages.screenTitle),
        customDocumentTitle: formatMessage(
          giftCardRedemptionScreenMessages.screenCustomDocumentTitle,
        ),
      },
    }),
    [formatMessage, navigatorScreenOptions, screenWithMobileHeaderOptions],
  );
};

// Screen options for the loyalty tab stack.
export const useAccountTabStackOptions = () => {
  const { formatMessage } = useIntl();

  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: true,
  });

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
        contentStyle: TRANSPARENT_CONTENT_STYLE,
      },
      AccountMenu: {
        ...DEFAULT_SCREEN_OPTIONS,
        customDocumentTitle: formatMessage(messages.pageTitleAccountMenu),
      },
      Profile: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(messages.accountProfileTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountInfo),
      },
      CreditAndPromoCodes: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(messages.accountCreditAndPromoCodesTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountCreditAndPromoCodes),
      },
      PaymentAndGiftCards: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(messages.accountPaymentsTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountPayments),
        headerShown: false,
        backgroundColor: theme.colors.OPACITY.TRANSPARENT,
        presentation: 'transparentModal' as const,
        contentStyle: TRANSPARENT_CONTENT_STYLE,
      },
      AccountGiftCards: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(messages.accountGiftCardsTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountGiftCards),
      },
      Addresses: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(messages.accountAddressesTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountAddresses),
      },
      Orders: {
        ...DEFAULT_SCREEN_OPTIONS,
        headerShown: false,
        title: formatMessage(messages.accountOrdersTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountOrders),
      },
      Favorites: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(messages.accountFavoritesTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountFavorites),
      },
      AccountDietaryRestrictions: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(accountMessages.dietaryRestrictionsTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountDietaryRestrictions),
      },
      NotificationPreferences: {
        ...DEFAULT_SCREEN_OPTIONS,
        title: formatMessage(accountMessages.notificationPreferencesTitle),
        customDocumentTitle: formatMessage(messages.pageTitleAccountNotificationPreferences),
      },
    }),
    [navigatorScreenOptions, formatMessage],
  );
};

// Screen options for the modal stack.
export const useModalStackOptions = ({ navigation }: ModalStackScreenProps) => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  const navigatorScreenOptions = useModalStackNavigatorScreenOptions({
    allowsTransparentModals: true,
    close: { onPress: navigation.goBack },
  });

  const defaultScreenOptions = useModalStackScreenOptions();

  const visibleHeader = useStackHeader({
    ...defaultScreenOptions,
    mobileHeaderShown: true,
  });

  const screenOptions = useMemo<NativeStackNavigationOptions>(
    () => ({ ...defaultScreenOptions, header: visibleHeader }),
    [defaultScreenOptions, visibleHeader],
  );

  const modalOptions = useMobileTransparentModalStackScreenOptions({
    ...defaultScreenOptions,
    mobileHeaderShown: true,
  });

  const bagScreenOptions = useMemo(
    () => ({
      ...defaultScreenOptions,
      ...modalOptions,
      headerShown: false,
    }),
    [defaultScreenOptions, modalOptions],
  );

  // ─────────────────────────────────────────────────────────────────────

  // Other than the navigator/default, all should either be:
  // - screenOptions - for when the screen is a screen.
  // - modalOptions - for when the screen is a modal.
  // - some edge case like the bag.
  return useMemo(
    () => ({
      Navigator: { ...navigatorScreenOptions },

      // Ordering flow is all visible headers.
      Customization: { ...screenOptions },
      CustomizationEdit: { ...screenOptions },
      CustomizationDelivery: { ...screenOptions },
      CustomizationDeliveryEdit: { ...screenOptions },
      ProductDetails: { ...screenOptions },
      ProductDetailsLocation: { ...modalOptions },

      UpsellScreen: {
        ...modalOptions,
        headerShown: false,
      },

      OrderStatus: {
        ...screenOptions,
        headerBackVisible: false,
        headerLeft: () => null,
        customDocumentTitle: formatMessage(messages.pageTitleOrderStatus),
      },
      ShareLineItem: { ...screenOptions },

      // Bag is shown as a drawer on desktop.
      Bag: {
        ...bagScreenOptions,
        customDocumentTitle: formatMessage(messages.pageTitleCart),
      },

      // Modal for managing payment methods, renders the customer sheet on native.
      Payments: {
        ...modalOptions,
        title: formatMessage(messages.accountPaymentsTitle),
        headerShown: false,
        backgroundColor: theme.colors.OPACITY.TRANSPARENT,
        presentation: 'transparentModal' as const,
        contentStyle: TRANSPARENT_CONTENT_STYLE,
      },

      Chat: {
        ...modalOptions,
        headerShown: false,
      },

      ReorderScreen: {
        ...modalOptions,
        headerShown: false,
      },

      // Reorder flow shows as a transparent modal in desktop.
      ReorderConfirmLocation: { ...modalOptions },
      ReorderChangeLocation: { ...modalOptions },
      ReorderConflictReview: { ...modalOptions },
      ReorderActiveBagWarning: { ...modalOptions },
      RateOrder: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
        title: formatMessage(messages.rateOrderTitle),
      },

      // These are transparent forms used in desktop as well.
      DietaryRestrictions: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
      },

      CreditAdd: {
        ...screenOptions,
        title: formatMessage(messages.accountScanToPayEnterPromoCode),
      },

      // These are for the mobile account, aren't used in desktop.
      CreditDetails: {
        ...screenOptions,
        title: formatMessage(messages.accountCreditMobileLabel),
      },

      // OneTrust Personal Data Management
      PersonalData: {
        ...screenOptions,
        title: formatMessage(messages.personalDataTitle),
      },
      PersonalDataDetails: { ...modalOptions },

      DeliveryOrderInFlight: { ...modalOptions },

      AddGiftCard: {
        ...screenOptions,
        title: formatMessage(messages.accountGiftCardsAddGiftCard),
      },
      RedeemGiftCard: {
        ...screenOptions,
        title: formatMessage(redeemGiftCardScreenMessages.screenTitle),
      },

      ScanAtCheckout: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
        headerShown: false,
      },

      ForgotToScan: {
        ...screenOptions,
        headerShown: !currentBreakpoint.isXS,
        mobileHeaderShown: false,
        title: formatMessage(forgotToScanMessages.screenTitle),
        customDocumentTitle: formatMessage(forgotToScanMessages.screenCustomDocumentTitle),
      },

      // OrderProduct - Deep linking to order a product.
      OrderProductConfirmProduct: {
        ...modalOptions,
        headerBackVisible: false,
        headerLeft: () => null,
      },
      OrderProductConfirmLocation: { ...modalOptions },
      OrderProductSearchLocation: { ...modalOptions },
    }),
    [
      navigatorScreenOptions,
      screenOptions,
      modalOptions,
      bagScreenOptions,
      formatMessage,
      currentBreakpoint.isXS,
    ],
  );
};

// Screen options for the auth stack.
export const useAuthStackOptions = ({ navigation }: AuthStackScreenProps) => {
  const { formatMessage } = useIntl();

  const navigatorScreenOptions = useModalStackNavigatorScreenOptions({
    mobileHeaderShown: true,
    close: { onPress: navigation.goBack },
  });

  return useMemo(
    () => ({
      Navigator: { ...navigatorScreenOptions },
      JoinOrSignIn: {
        ...DEFAULT_SCREEN_OPTIONS,
        customDocumentTitle: formatMessage(messages.pageTitleJoinOrSignIn),
      },
      Login: {
        ...DEFAULT_SCREEN_OPTIONS,
        customDocumentTitle: formatMessage(messages.pageTitleSignIn),
      },
      Join: {
        ...DEFAULT_SCREEN_OPTIONS,
        customDocumentTitle: formatMessage(messages.pageTitleJoin),
      },
    }),
    [navigatorScreenOptions, formatMessage],
  );
};

// Screen options for error stack.
export const useErrorStackOptions = () => {
  const navigatorScreenOptions = useTabStackNavigatorScreenOptions({
    mobileHeaderShown: false,
  });

  return useMemo(
    () => ({
      Navigator: {
        ...navigatorScreenOptions,
      },
      NotFound: {
        ...DEFAULT_SCREEN_OPTIONS,
      },
    }),
    [navigatorScreenOptions],
  );
};

// ———————— STACK HEADER ——————————————————————

type NativeStackHeaderProp = NativeStackNavigationOptions['header'];

/**
 * Returns a function which may be passed to the `header` property
 * of a stack navigator's screen options.
 */
const useStackHeader = (props?: UseStackHeaderProps): NativeStackHeaderProp => {
  const { currentBreakpoint } = useResponsive();
  const { mobileHeaderShown } = props ?? {};

  const getNavbar = useCallback(() => <Navbar />, []);

  if (currentBreakpoint.isXS && mobileHeaderShown) {
    return undefined;
  }

  if (currentBreakpoint.isXS && !mobileHeaderShown) {
    return () => <View />;
  }

  return getNavbar;
};

type UseStackHeaderProps = Readonly<{
  mobileHeaderShown?: boolean;
}>;

// ———————— CONTENT STYLE ——————————————————————

type ContentStyleOption = NativeStackNavigationOptions['contentStyle'];

const DEFAULT_CONTENT_STYLE: ContentStyleOption = {
  backgroundColor: theme.colors.APP_BACKGROUND,
};

const TRANSPARENT_CONTENT_STYLE: ContentStyleOption = {
  backgroundColor: theme.colors.OPACITY.TRANSPARENT,
};

/**
 * The value of a `contentStyle` property for screens
 * which appear as "transparent modals" in the desktop form factor.
 */
const useMobileTransparentScreenContentStyle = (): ContentStyleOption => {
  const { currentBreakpoint } = useResponsive();

  return currentBreakpoint.isXS ? DEFAULT_CONTENT_STYLE : TRANSPARENT_CONTENT_STYLE;
};

type PresentationOption = NativeStackNavigationOptions['presentation'];

/**
 * The value of a 'presentation` property for screens
 * which appear as "transparent modals" on web.
 */
const useMobileTransparentModalScreenPresentation = (): PresentationOption => {
  const { currentBreakpoint } = useResponsive();

  return currentBreakpoint.isXS ? 'modal' : 'transparentModal';
};

const DEFAULT_HEADER_TITLE_STYLE = TYPE_CONFIG.BODY[
  '18'
] as NativeStackNavigationOptions['headerTitleStyle'];

// ———————— SCREEN OPTIONS ——————————————————————

/**
 * Default value of `screenOptions` for all screens.
 */
const DEFAULT_SCREEN_OPTIONS = {
  title: 'sweetgreen',
  headerBackImageSource: getCustomBackButtonIcon(),
};

/**
 * Default value of `screenOptions` for a native stack navigator.
 */
const DEFAULT_STACK_SCREEN_OPTIONS: NativeStackNavigationOptions = {
  ...DEFAULT_SCREEN_OPTIONS,
  headerShadowVisible: false,
  headerTintColor: theme.colors.GREEN_1,
  headerStyle: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  headerTitleStyle: DEFAULT_HEADER_TITLE_STYLE,
  headerTitleAlign: 'center',
  headerBackTitleVisible: false,
  headerTitle: '',
  contentStyle: DEFAULT_CONTENT_STYLE,
};

const DEFAULT_TABS_OPTIONS: BottomTabNavigationOptions = {
  headerShown: false,
};

/**
 * The value of `screenOptions` for a native stack navigator contained in a tab.
 */
const useTabStackNavigatorScreenOptions = (
  props?: UseStackHeaderProps,
): NativeStackNavigationOptions => {
  const header = useStackHeader(props);

  return {
    ...DEFAULT_STACK_SCREEN_OPTIONS,
    headerShown: true,
    header,
  };
};

/**
 * The value of `screenOptions` for a native stack navigator contained in a modally presented screen.
 *
 * @param {Object} options - additional modal stack options
 * @param {HeaderCloseIconItemProps} options.close - Specifies the close button `onPress` handler, accessibility strings, and `testID`.
 * @param {boolean} options.allowsTransparentModals - If true, configures the contentStyle property to have a transparent background.
 */
const useModalStackNavigatorScreenOptions = (
  props: UseModalStackNavigatorScreenOptionsProps,
): NativeStackNavigationOptions => {
  const { close, allowsTransparentModals, ...stackHeaderProps } = props;

  const { currentBreakpoint } = useResponsive();

  const header = useStackHeader(stackHeaderProps);
  const headerRight = useHeaderRight({ close });

  const contentStyle =
    allowsTransparentModals && !currentBreakpoint.isXS
      ? TRANSPARENT_CONTENT_STYLE
      : DEFAULT_CONTENT_STYLE;

  const options: NativeStackNavigationOptions = {
    ...DEFAULT_STACK_SCREEN_OPTIONS,
    headerShown: true,
    header,
    headerRight,
    contentStyle,
  };

  return options;
};

type UseModalStackNavigatorScreenOptionsProps = UseStackHeaderProps & {
  close?: HeaderButtonProps;
  allowsTransparentModals?: boolean;
};

/**
 * The value of `screenOptions` native stack navigator contained in a modally presented screen.
 */
const useModalStackScreenOptions = (): NativeStackNavigationOptions => {
  return {
    ...DEFAULT_SCREEN_OPTIONS,
    contentStyle: DEFAULT_CONTENT_STYLE,
  };
};

/**
 * The value of `options` for a stack screen presented as a "transparent modal" on web.
 */
const useMobileTransparentModalStackScreenOptions = (
  props?: UseStackHeaderProps,
): NativeStackNavigationOptions => {
  const { currentBreakpoint } = useResponsive();
  const header = useStackHeader(props);
  const contentStyle = useMobileTransparentScreenContentStyle();
  const options: NativeStackNavigationOptions = {
    ...DEFAULT_SCREEN_OPTIONS,
    presentation: currentBreakpoint.isXS ? 'card' : 'transparentModal',
    headerShown: currentBreakpoint.isXS,
    header,
    contentStyle,
  };

  return options;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  navBarHomeTitle: {
    defaultMessage: 'Home',
    description: 'Nav Bar | Home | Title',
  },
  navBarMenuTitle: {
    defaultMessage: 'Menu',
    description: 'Nav Bar | Menu | Title',
  },
  navBarAccountTitle: {
    defaultMessage: 'Account',
    description: 'Nav Bar | Account | Title',
  },
  accountProfileTitle: {
    defaultMessage: 'Account info',
    description: 'Account | Profile | Title',
  },
  accountCreditAndPromoCodesTitle: {
    defaultMessage: 'Credit & promo codes',
    description: 'Account | Credit And Promo Codes | Title',
  },
  accountPaymentsTitle: {
    defaultMessage: 'Payments',
    description: 'Account | Payments | Title',
  },
  accountGiftCardsTitle: {
    defaultMessage: 'Gift card',
    description: 'Account | Gift Cards | Title',
  },
  accountAddressesTitle: {
    defaultMessage: 'Addresses',
    description: 'Account | Addresses | Title',
  },
  accountOrdersTitle: {
    defaultMessage: 'Orders',
    description: 'Account | Orders | Title',
  },
  accountFavoritesTitle: {
    defaultMessage: 'Favorites',
    description: 'Account | Favorites | Title',
  },
  rateOrderTitle: {
    defaultMessage: 'Rate your order',
    description: 'Rate Order | Title',
  },
  accountScanToPayEnterPromoCode: {
    defaultMessage: 'Enter promo code',
    description: 'Account | Scan To Pay | Enter Promo Code',
  },
  accountCreditMobileLabel: {
    defaultMessage: 'Available credit',
    description: 'Account | Credit | Mobile Label',
  },
  personalDataTitle: {
    defaultMessage: 'Sweetgreen Data Subject Requests',
    description: 'Personal Data | Title',
  },
  accountGiftCardsAddGiftCard: {
    defaultMessage: 'Add gift card',
    description: 'Account | Gift Cards | Add Gift Card',
  },
  pageTitleHome: {
    defaultMessage: 'Home',
    description: 'Page Title | Home',
  },
  pageTitleLocations: {
    defaultMessage: 'Locations',
    description: 'Page Title | Locations',
  },
  pageTitleOrderStatus: {
    defaultMessage: 'Order Status',
    description: 'Page Title | Order Status',
  },
  pageTitleCart: {
    defaultMessage: 'Cart',
    description: 'Page Title | Cart',
  },
  pageTitleAccountMenu: {
    defaultMessage: 'Account',
    description: 'Page Title | Account Menu',
  },
  pageTitleAccountInfo: {
    defaultMessage: 'Account Info',
    description: 'Page Title | Account Info',
  },
  pageTitleAccountCreditAndPromoCodes: {
    defaultMessage: 'Credit & Promo Codes',
    description: 'Page Title | Account Credit And Promo Codes',
  },
  pageTitleAccountPayments: {
    defaultMessage: 'Payments',
    description: 'Page Title | Account Payments',
  },
  pageTitleAccountGiftCards: {
    defaultMessage: 'Gift Cards',
    description: 'Page Title | Account Gift Cards',
  },
  pageTitleAccountAddresses: {
    defaultMessage: 'Addresses',
    description: 'Page Title | Account Addresses',
  },
  pageTitleAccountOrders: {
    defaultMessage: 'Orders',
    description: 'Page Title | Account Orders',
  },
  pageTitleAccountFavorites: {
    defaultMessage: 'Favorites',
    description: 'Page Title | Account Favorites',
  },
  pageTitleAccountDietaryRestrictions: {
    defaultMessage: 'Dietary Restrictions',
    description: 'Page Title | Account Dietary Restrictions',
  },
  pageTitleAccountNotificationPreferences: {
    defaultMessage: 'Notification Preferences',
    description: 'Page Title | Account Notification Preferences',
  },
  pageTitleJoinOrSignIn: {
    defaultMessage: 'Join or Sign In',
    description: 'Page Title | Join or Sign In',
  },
  pageTitleSignIn: {
    defaultMessage: 'Sign In',
    description: 'Page Title | Sign In',
  },
  pageTitleJoin: {
    defaultMessage: 'Join',
    description: 'Page Title | Join',
  },
});
